import { useAuth } from '../contexts/AuthContext';

/**
 * Single source of truth for role-aware UI gating (STAFF_ACCESS_PLAN §7).
 *
 * A venue object carrying `staff_capabilities` means the logged-in user is
 * STAFF for that venue (the backend tags staffed venues at login/profile).
 * Owners' venues never carry the tag. The backend enforces everything —
 * this hook only decides what to SHOW.
 */
export interface VenueRole {
  isStaff: boolean;
  isOwner: boolean;
  roleLabel: string | null;
  capabilities: string[];
  /** Owner: always true. Staff: true only if the capability was granted. */
  can: (capability: string) => boolean;
}

export function useVenueRole(): VenueRole {
  const { user } = useAuth();
  const venue = user?.venues?.[0] as (Record<string, unknown> | undefined);

  const staffCapabilities = venue?.staff_capabilities as string[] | undefined;
  const isStaff = Array.isArray(staffCapabilities);
  const capabilities = isStaff ? staffCapabilities : [];

  return {
    isStaff,
    isOwner: !isStaff,
    roleLabel: (venue?.staff_role_label as string | undefined) ?? null,
    capabilities,
    can: (capability: string) => !isStaff || capabilities.includes(capability),
  };
}
